/**
 * Teslyn Mobile, the phone app (batch 7; docs/specs/mobile/teslyn-mobile.md). The rules the phone uses and nothing
 * else works out: who may use it and who may record a flight (§3), a flight started at the airport and its flight
 * time after landing (§4: the fields, their checks, the daily entry row with the desktop's defaults), the states of a
 * phone flight and the desktop's hours check (§4.3, §4.5), the push token check (§6.1) and the thinning of a station
 * chart (§5.2).
 *
 * A phone flight is an ordinary `daily_entry` of type Flight with `entry_origin = 'mobile'`, `planned_flight_h` and,
 * after landing, `flight_time_h` (migration 20261028160000, Field SQLite 0066). Teslyn Field finishes it.
 */
import { maxFlightHours } from './aircraft.js';
import { parseTime, typedTime } from './daily-entry.js';
import { addIsoDays, isoDateToDate, localIsoDate } from './dates.js';
import { dipstickFromText, dipstickUnit, dipstickWords, validateDipstick } from './engine-oil.js';
import { FLIGHT_STATUS_HOURS, flightTimeLabel } from './flight-status.js';
import { crewPersonName, removedPersonLabel } from './staff.js';
import {
  entryForType,
  entryShape,
  fuelFromLitres,
  litresFromFuel,
  newFlightEntryDefaults,
  previousFlightEntry,
} from './entry-card.js';
import { MOBILE_APP_IDS, mobileAppName } from './mobile-release.js';
import type { Aircraft, DailyEntry, FuelSource, Person, Uuid, ValidationIssue } from './model.js';
import { thinTrail } from './trail-kml.js';

// ------------------------------------------------------------------------------------------------ access (§3)

/**
 * The sign-in roles (`membership.role`) that open the phone app: every staff role and Pilot (R156, decision 917: a
 * pilot-only sign-in sees the map and fuel receipt and MR photos only, `mobileTabs` in pilot.ts).
 */
export const MOBILE_STAFF_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
  'pilot',
] as const;
export type MobileStaffRole = (typeof MOBILE_STAFF_ROLES)[number];

/** The roles RLS lets write `daily_entry` (20261018000100_m1_policies.sql): office staff read only. */
export const PHONE_FLIGHT_WRITE_ROLES: readonly MobileStaffRole[] = [
  'tenant_admin',
  'manager',
  'field_crew',
  'data_processor',
];

export interface MobileWorkspace {
  readonly tenant_id: Uuid;
  readonly tenant_name: string;
  readonly roles: readonly string[];
}

export type MobileAccess =
  | { readonly ok: true; readonly workspaces: readonly MobileWorkspace[] }
  | { readonly ok: false; readonly message: string };

/**
 * Whether a signed-in person may use the phone app (staff and pilots, F01 and R156): the organisations where they hold
 * at least one staff role or Pilot. Clients and platform admins without one are told why, in words.
 */
export function mobileAccess(
  memberships: readonly { readonly tenant_id: Uuid; readonly tenant_name: string; readonly role: string }[],
  clientOf: readonly string[] = [],
): MobileAccess {
  const by = new Map<Uuid, { tenant_name: string; roles: string[] }>();
  for (const m of memberships) {
    if (!(MOBILE_STAFF_ROLES as readonly string[]).includes(m.role)) continue;
    const w = by.get(m.tenant_id) ?? { tenant_name: m.tenant_name, roles: [] };
    if (!w.roles.includes(m.role)) w.roles.push(m.role);
    by.set(m.tenant_id, w);
  }
  const workspaces = [...by.entries()]
    .map(([tenant_id, w]) => ({ tenant_id, tenant_name: w.tenant_name, roles: w.roles }))
    .sort((a, b) => a.tenant_name.localeCompare(b.tenant_name));
  if (workspaces.length > 0) return { ok: true, workspaces };
  const org = clientOf[0];
  return {
    ok: false,
    message: org
      ? `Teslyn Mobile is for ${org} staff for now. Clients use Teslyn Cloud in the browser.`
      : 'Teslyn Mobile is for survey company staff. Your sign-in has no staff role in any organisation. Ask your administrator to add you.',
  };
}

/** Start and land flights on the phone (tenant admins, managers, field crew, data processors). */
export function canRecordPhoneFlight(roles: readonly string[]): boolean {
  return roles.some((r) => (PHONE_FLIGHT_WRITE_ROLES as readonly string[]).includes(r));
}

// ------------------------------------------------------------------------------------------------ flights (§4)

/** The columns a phone flight adds to `daily_entry` (on `DailyEntry` in model.ts; absent on older rows). */
export type PhoneFlightColumns = Pick<DailyEntry, 'planned_flight_h' | 'flight_time_h' | 'entry_origin'>;

/** A daily entry as the phone and Teslyn Field 2026.10.13 or newer know it: `DailyEntry` itself. */
export type PhoneDailyEntry = DailyEntry;

/** The most hours a flight time or planned flight time may be (a day). */
export const PHONE_FLIGHT_MAX_H = 24;
/** A flight time this far from the planned time gets a note (§4.2). */
export const PHONE_PLANNED_NOTE_H = 0.5;
/** The desktop's hours split may differ from the phone's flight time by this much (§4.5). */
export const PHONE_HOURS_TOLERANCE_H = 0.05;

/** What the phone's "New flight" form holds, as typed. */
export interface PhoneFlightForm {
  readonly aircraft_id: Uuid | null;
  /** As typed: 512, 0512, 05:12. */
  readonly takeoff: string;
  /** As typed, in hours (3.5) or hours and minutes (3:30). */
  readonly planned: string;
  /** As typed, in the aircraft's fuel unit; '' for no refuel. */
  readonly refuel: string;
  readonly fuel_source: FuelSource;
  readonly fuel_location: string;
  /** As typed, litres; '' is 0. */
  readonly oil_l: string;
  readonly oil_r: string;
  /**
   * The oil level on the dipstick before oil is added, as typed, in the aircraft's dipstick unit (R150, decision 920;
   * engine-oil.ts); '' or absent is not read.
   */
  readonly oil_dipstick_l?: string;
  readonly oil_dipstick_r?: string;
  readonly pilot_id: Uuid | null;
  /**
   * The flight time after landing, as typed in hours (3.2 or 3:12), on the form that changes a flight (§4.7); absent
   * on "New flight", where it is added after landing.
   */
  readonly flight_time?: string;
  /**
   * The flight number as typed (R179, 3 Nov 2026: "Need to be able to add flight number to flight from mobile app");
   * '' or absent is none (a ferry, or not known yet). A pilot-only sign-in has no such box: a pilot's flight has no
   * number (decision 971, `daily_entry_pilot_insert`).
   */
  readonly flight_number?: string;
  /**
   * The route of a pilot's ferry flight (decision 996: both "Start a flight" buttons make the same ferry, its route
   * in the comment "Ferry Ceduna to Forrest"); absent on a staff flight.
   */
  readonly from?: string;
  readonly to?: string;
}

export const EMPTY_PHONE_FLIGHT_FORM: PhoneFlightForm = {
  aircraft_id: null,
  takeoff: '',
  planned: '',
  refuel: '',
  fuel_source: null,
  fuel_location: '',
  oil_l: '',
  oil_r: '',
  oil_dipstick_l: '',
  oil_dipstick_r: '',
  pilot_id: null,
  flight_number: '',
};

/** The desktop's limit on a flight number (`validateDailyEntry`: 0 to 9999). */
export const FLIGHT_NUMBER_MAX = 9999;

/**
 * A flight number as typed on the phone: a whole number 0 to 9999; '' is none (null); anything else is undefined (not
 * a flight number).
 */
export function typedFlightNumber(text: string | undefined): number | null | undefined {
  const t = (text ?? '').trim();
  if (t === '') return null;
  if (!/^\d{1,4}$/.test(t)) return undefined;
  const n = Number(t);
  return n > FLIGHT_NUMBER_MAX ? undefined : n;
}

/**
 * Hours as typed on the phone: `3.2`, `3,2`, `3:12` (hours and minutes) or `3h12`. Null when it is not a time;
 * empty is null too.
 */
export function typedHours(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (t === '') return null;
  const hm = /^(\d{1,2})\s*[:h]\s*([0-5]?\d)$/i.exec(t);
  if (hm) return Number(hm[1]) + Number(hm[2]) / 60;
  if (!/^\d{0,2}(\.\d{0,2})?$/.test(t) || t === '.') return null;
  return Number(t);
}

/** `3.2 h`, one decimal. */
export const hoursText = (h: number): string => `${h.toFixed(1)} h`;

const number = (text: string): number | null => {
  const t = text.trim().replace(',', '.');
  if (t === '') return 0;
  if (!/^\d+(\.\d+)?$/.test(t)) return null;
  return Number(t);
};

/** A stored number as a box shows it again: `3.5`, `3.33` (at most two decimals, so a stored 3:20 reads 3.33). */
const numberText = (h: number | null | undefined): string =>
  h === null || h === undefined ? '' : String(Math.round(h * 100) / 100);

/** A blank box is no value: an issue only when the value is required (the flight time of "Add flight time"). */
function hoursIssue(
  field: string,
  what: string,
  h: number | null,
  text: string,
  required: boolean,
): ValidationIssue | null {
  if (text.trim() === '') return required ? { field, message: `Enter the ${what}.` } : null;
  if (h === null)
    return { field, message: `"${text.trim()}" is not a time. Type hours, for example 3.5 or 3:30.` };
  if (!(h > 0)) return { field, message: `The ${what} must be more than 0 h.` };
  if (h > PHONE_FLIGHT_MAX_H)
    return { field, message: `The ${what} must be at most ${PHONE_FLIGHT_MAX_H} h.` };
  return null;
}

export interface PhoneFlightContext {
  /** The project's aircraft (deleted ones are not offered). */
  readonly aircraft: readonly Aircraft[];
  /** The project's people (any role). */
  readonly people: readonly Pick<Person, 'id' | 'name' | 'deleted_at'>[];
  /**
   * The flight being changed (§4.7), or absent on "New flight": its own aircraft and pilot are accepted as they are,
   * even when the register no longer lists them (the phone reads the live register only).
   */
  readonly entry?: Pick<DailyEntry, 'aircraft_id' | 'pilot_id'> | null;
  /**
   * The project's daily entries the phone holds, for the flight-number warning (R179: a number another flight of the
   * aircraft already has, `validateDailyEntry`'s rule, as a warning); absent: no such warning.
   */
  readonly entries?: readonly DailyEntry[];
  /** The id of the flight being changed, left out of the flight-number warning. */
  readonly entryId?: Uuid | null;
}

/**
 * The checks of "New flight" (§4.1) and of changing a flight (§4.7). Nothing is required (MB, 2 Nov 2026: "no
 * minimum fields"): every box may stay blank, and Teslyn Field asks for what it needs when the crew finish the entry.
 * A value that is there must be a value: a time, hours more than 0 and at most 24, a refuel of 0 or more, oil 0 to
 * 12 L, a dipstick reading within its unit's limit, an aircraft and a pilot of the register. In the desktop's words
 * where it has them.
 */
export function validatePhoneFlight(form: PhoneFlightForm, ctx: PhoneFlightContext): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const add = (field: string, message: string): void => {
    issues.push({ field, message });
  };
  const known = (
    id: Uuid | null,
    list: readonly { readonly id: Uuid; readonly deleted_at: string | null }[],
    kept: Uuid | null | undefined,
  ): boolean => id === null || id === kept || list.some((x) => x.id === id && x.deleted_at === null);
  if (!known(form.aircraft_id, ctx.aircraft, ctx.entry?.aircraft_id))
    add('aircraft_id', 'That aircraft is no longer in the register. Choose another, or leave it blank.');
  const aircraft = ctx.aircraft.find((a) => a.id === form.aircraft_id) ?? null;
  const t = typedTime(form.takeoff);
  if (!t.ok) add('takeoff_time', t.message);
  const planned = hoursIssue(
    'planned_flight_h',
    'planned flight time',
    typedHours(form.planned),
    form.planned,
    false,
  );
  if (planned) issues.push(planned);
  if (form.flight_time !== undefined) {
    const flown = hoursIssue(
      'flight_time_h',
      'flight time',
      typedHours(form.flight_time),
      form.flight_time,
      false,
    );
    if (flown) issues.push(flown);
  }
  if (number(form.refuel) === null) add('fuel_load_l', 'The refuel must be a number, 0 or more.');
  const shape = entryShape('flight', aircraft);
  for (const [field, text, shown] of [
    ['oil_l', form.oil_l, shape.oilBoxes >= 1],
    ['oil_r', form.oil_r, shape.oilBoxes >= 2],
  ] as const) {
    if (!shown) continue;
    const v = number(text);
    if (v === null || v > 12) add(field, 'Oil added must be 0 to 12 L.');
  }
  // R150, decision 920: the optional dipstick readings beside the oil boxes, in the aircraft's unit
  const unit = dipstickUnit(aircraft);
  for (const [field, text, shown, engine] of [
    [
      'oil_dipstick_l',
      form.oil_dipstick_l,
      shape.dipstickBoxes >= 1,
      shape.dipstickBoxes === 1 ? 'single' : 'left',
    ],
    ['oil_dipstick_r', form.oil_dipstick_r, shape.dipstickBoxes >= 2, 'right'],
  ] as const) {
    if (!shown) continue;
    const message = validateDipstick(dipstickFromText(text ?? ''), unit, engine);
    if (message) add(field, message);
  }
  if (!known(form.pilot_id, ctx.people, ctx.entry?.pilot_id))
    add('pilot_id', 'That pilot is no longer in the staff register. Choose another, or leave it blank.');
  // R179: the desktop's words; blank is a ferry or a number not known yet
  if (typedFlightNumber(form.flight_number) === undefined)
    add('flt_num', `Enter a valid flight number (0 to ${FLIGHT_NUMBER_MAX}), or leave it blank.`);
  // decision 996: a pilot's route, each place at most FERRY_PLACE_MAX characters (the comment is the record)
  for (const [field, text] of [
    ['from', form.from ?? ''],
    ['to', form.to ?? ''],
  ] as const)
    if (text.trim().length > FERRY_PLACE_MAX) add(field, `A place is at most ${FERRY_PLACE_MAX} characters.`);
  return issues;
}

/**
 * The flight-number warning (R179): the desktop refuses a number another flight of the same aircraft already has
 * (`validateDailyEntry`: "Flight number 12 is already used for this aircraft."; the database has no such rule), so
 * the phone warns in words and still saves. Null when there is nothing to say: no number, no aircraft, or the number
 * is free. The flight being changed (`entryId`) is not its own clash.
 */
export function phoneFlightNumberWarning(
  form: Pick<PhoneFlightForm, 'aircraft_id' | 'flight_number'>,
  ctx: Pick<PhoneFlightContext, 'aircraft' | 'entries' | 'entryId'>,
): string | null {
  const n = typedFlightNumber(form.flight_number);
  if (n === null || n === undefined || n <= 0 || !form.aircraft_id || !ctx.entries) return null;
  const clash = ctx.entries.find(
    (e) =>
      e.deleted_at === null &&
      e.id !== (ctx.entryId ?? null) &&
      e.entry_type === 'flight' &&
      e.aircraft_id === form.aircraft_id &&
      e.flt_num === n,
  );
  if (!clash) return null;
  const a = ctx.aircraft.find((x) => x.id === form.aircraft_id);
  return `Flight number ${n} is already used for ${a?.registration ?? 'this aircraft'} (${phoneDateWords(clash.date)}). Teslyn Field asks for another number before it saves the entry.`;
}

/**
 * Warnings that never block the save: a planned flight longer than the aircraft's maximum endurance, and a refuel
 * the desktop will not save as it is (its rule: a refuel needs its source and location, a source needs an amount;
 * `validateDailyEntry`), so the crew know Teslyn Field will ask.
 */
export function phoneFlightWarnings(form: PhoneFlightForm, ctx: PhoneFlightContext): string[] {
  const out: string[] = [];
  const a = ctx.aircraft.find((x) => x.id === form.aircraft_id) ?? null;
  const h = typedHours(form.planned);
  const max = a ? maxFlightHours(a) : null;
  if (a && h !== null && max !== null && h > max + 1e-9)
    out.push(
      `The planned flight time (${hoursText(h)}) is longer than the maximum endurance of ${a.registration} (${hoursText(max)}).`,
    );
  const refuel = number(form.refuel);
  if (refuel !== null && refuel > 0) {
    if (form.fuel_source === null)
      out.push('No refuel source chosen. Teslyn Field asks for it before it saves the entry.');
    if (form.fuel_location.trim() === '')
      out.push('No refuel location. Teslyn Field asks for it before it saves the entry.');
  } else if (refuel === 0 && form.fuel_source !== null)
    out.push('A refuel source with no amount. Teslyn Field asks for the amount before it saves the entry.');
  const clash = phoneFlightNumberWarning(form, ctx);
  if (clash) out.push(clash);
  return out;
}

/** The fuel unit of the form's refuel box: the aircraft's (L or US gal). */
export function phoneFuelUnitLabel(a: Aircraft | null | undefined): string {
  return a?.fuel_unit === 'USG' ? 'US gal' : 'L';
}

/** How many oil boxes the form shows for the aircraft: 0, 1 or 2 (`entryShape`). */
export function phoneOilBoxes(a: Aircraft | null | undefined): 0 | 1 | 2 {
  return entryShape('flight', a ?? null).oilBoxes as 0 | 1 | 2;
}

export interface NewPhoneFlightInput {
  readonly id: Uuid;
  readonly project_id: Uuid;
  /** The phone's date, YYYY-MM-DD. */
  readonly date: string;
  /** The phone's clock, ISO (created_at and updated_at). */
  readonly nowIso: string;
  readonly form: PhoneFlightForm;
  readonly aircraft: Aircraft | null;
  /** The project's daily entries the phone knows (the previous flight's fuel location, R116). */
  readonly entries: readonly DailyEntry[];
}

const dayOfYear = (date: string): number => {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86_400_000) + 1;
};

/**
 * The `daily_entry` row of a flight started on the phone (§4.1): the form's values over the desktop's defaults for a
 * new flight entry (`entryForType`, `newFlightEntryDefaults`: starts 1 when the aircraft logs starts, the previous
 * flight's fuel location when none is typed), no flight number, hours 0, `entry_origin = 'mobile'`. Call it with a
 * form `validatePhoneFlight` accepts.
 */
export function newPhoneFlightEntry(input: NewPhoneFlightInput): PhoneDailyEntry {
  const { form, aircraft } = input;
  const takeoff = typedTime(form.takeoff);
  const blank: DailyEntry = {
    id: input.id,
    created_at: input.nowIso,
    updated_at: input.nowIso,
    updated_by: null,
    deleted_at: null,
    project_id: input.project_id,
    date: input.date,
    jday: dayOfYear(input.date),
    aircraft_id: form.aircraft_id,
    base_mag_position_id: null,
    ground_cal_position_id: null,
    test_line_position_id: null,
    pilot_id: form.pilot_id,
    pilot2_id: null,
    operator1_id: null,
    operator2_id: null,
    entry_type: 'flight',
    standby_type: null,
    // R179: the number typed, else none (the desktop's log import or the crew give it)
    flt_num: typedFlightNumber(form.flight_number) ?? null,
    takeoff_time: takeoff.ok ? takeoff.value : null,
    blocks: [],
    default_block_id: null,
    days_alloc: 0,
    svy_hours: 0,
    cbx_hours: 0,
    cal_hours: 0,
    fry_hours: 0,
    ext_hours: 0,
    fuel_used_l: 0,
    fuel_source: form.fuel_source,
    fuel_location: form.fuel_location.trim(),
    fuel_load_l: litresFromFuel(number(form.refuel) ?? 0, aircraft?.fuel_unit ?? 'L'),
    starts: 0,
    oil_l: number(form.oil_l) ?? 0,
    oil_r: number(form.oil_r) ?? 0,
    new_mr_ttis_h: null,
    op_comment: '',
    qc_comment: '',
    mobilization: false,
    log_file: null,
  };
  const changed = new Set<keyof DailyEntry>();
  if (blank.fuel_location !== '') changed.add('fuel_location');
  // R150: a reading only when one was typed, so the row names the columns only then (day-entry-card.md §3.2)
  const boxes = entryShape('flight', aircraft).dipstickBoxes;
  const dl = boxes >= 1 ? dipstickFromText(form.oil_dipstick_l ?? '') : null;
  const dr = boxes >= 2 ? dipstickFromText(form.oil_dipstick_r ?? '') : null;
  if (dl !== null && Number.isFinite(dl)) Object.assign(blank, { oil_dipstick_l: dl });
  if (dr !== null && Number.isFinite(dr)) Object.assign(blank, { oil_dipstick_r: dr });
  const entry = newFlightEntryDefaults(entryForType(blank, 'flight', aircraft), {
    aircraft,
    entries: input.entries,
    changed,
  });
  return {
    ...entry,
    planned_flight_h: typedHours(form.planned),
    flight_time_h: null,
    entry_origin: 'mobile',
  };
}

/** The fuel location the form starts with: the aircraft's previous flight's (R116), else blank. */
export function phoneFuelLocationDefault(
  entries: readonly DailyEntry[],
  aircraftId: Uuid | null,
  date: string,
): string {
  if (!aircraftId) return '';
  return (
    previousFlightEntry(entries, { id: '', aircraft_id: aircraftId, date, flt_num: null })?.fuel_location ??
    ''
  );
}

/**
 * The project's last fuel location (R178, MB 3 Nov 2026: "remember last fuel location and pre fill"): the fuel
 * location of the project's flight entry changed most recently (`updated_at`; the phone's own save counts at once
 * through the outbox), whatever its aircraft; blank when no flight of the project has one. Per project, since the
 * phone holds one project's entries; it needs nothing kept on the phone, so a reinstall or a second phone has it too.
 */
export function phoneLastFuelLocation(entries: readonly DailyEntry[]): string {
  let best: DailyEntry | null = null;
  for (const e of entries) {
    if (e.deleted_at !== null || e.entry_type !== 'flight' || e.fuel_location.trim() === '') continue;
    if (
      !best ||
      e.updated_at > best.updated_at ||
      (e.updated_at === best.updated_at && comparePhoneFlights(e, best) < 0)
    )
      best = e;
  }
  return best?.fuel_location.trim() ?? '';
}

/**
 * The aircraft a new flight starts with (R178: "a choice with only one option is filled in already"): the one live
 * aircraft the project's flights use, else the register's only live aircraft; null when there is a choice to make.
 * The phone holds the organisation's aircraft, not a project assignment, so the project's own flights say which
 * aircraft is its.
 */
export function phoneAircraftStart(
  aircraft: readonly Pick<Aircraft, 'id' | 'deleted_at'>[],
  entries: readonly Pick<DailyEntry, 'aircraft_id' | 'entry_type' | 'deleted_at'>[],
): Uuid | null {
  const live = aircraft.filter((a) => a.deleted_at === null).map((a) => a.id);
  const flown = new Set(
    entries
      .filter((e) => e.deleted_at === null && e.entry_type === 'flight' && e.aircraft_id !== null)
      .map((e) => e.aircraft_id as Uuid)
      .filter((id) => live.includes(id)),
  );
  if (flown.size === 1) return [...flown][0]!;
  return live.length === 1 ? live[0]! : null;
}

/**
 * The pilot a new flight starts with (R178): the one pilot on the project's crew (the `project` group of
 * `crewChoices`), else the register's only pilot; null when there is a choice to make.
 */
export function phonePilotStart(
  pilots: readonly { readonly id: Uuid; readonly group: 'project' | 'register' | 'former' }[],
): Uuid | null {
  const onProject = pilots.filter((c) => c.group === 'project');
  if (onProject.length === 1) return onProject[0]!.id;
  const live = pilots.filter((c) => c.group !== 'former');
  return live.length === 1 ? live[0]!.id : null;
}

/**
 * "New flight" as it opens (R178): the only aircraft and pilot chosen already, the project's last fuel location (or
 * the chosen aircraft's previous flight's, R116) filled in; every pre-filled box stays editable, and a chosen choice
 * clears on a second press as any other.
 */
export function phoneFlightFormStart(ctx: {
  readonly aircraft: readonly Pick<Aircraft, 'id' | 'deleted_at'>[];
  readonly pilots: readonly { readonly id: Uuid; readonly group: 'project' | 'register' | 'former' }[];
  readonly entries: readonly DailyEntry[];
  readonly date: string;
}): PhoneFlightForm {
  const aircraft_id = phoneAircraftStart(ctx.aircraft, ctx.entries);
  return {
    ...EMPTY_PHONE_FLIGHT_FORM,
    aircraft_id,
    pilot_id: phonePilotStart(ctx.pilots),
    fuel_location:
      phoneLastFuelLocation(ctx.entries) || phoneFuelLocationDefault(ctx.entries, aircraft_id, ctx.date),
  };
}

/** The refuel as the form shows it again, in the aircraft's unit ('' for none). */
export function phoneRefuelText(entry: Pick<DailyEntry, 'fuel_load_l'>, a: Aircraft | null): string {
  if (!(entry.fuel_load_l > 0)) return '';
  const v = fuelFromLitres(entry.fuel_load_l, a?.fuel_unit ?? 'L');
  return a?.fuel_unit === 'USG' ? v.toFixed(1) : String(Math.round(v));
}

/** The check of "Add flight time" (§4.2): the one box the button is for, so a value is needed there. */
export function validatePhoneLanding(text: string): ValidationIssue[] {
  const issue = hoursIssue('flight_time_h', 'flight time', typedHours(text), text, true);
  return issue ? [issue] : [];
}

/** The notes under "Add flight time": longer than the endurance, far from the planned time. */
export function phoneLandingNotes(
  flightH: number,
  entry: Pick<PhoneDailyEntry, 'planned_flight_h'>,
  a: Aircraft | null,
): string[] {
  const out: string[] = [];
  const max = a ? maxFlightHours(a) : null;
  if (a && max !== null && flightH > max + 1e-9)
    out.push(
      `${hoursText(flightH)} is longer than the maximum endurance of ${a.registration} (${hoursText(max)}). Check the flight time.`,
    );
  const planned = entry.planned_flight_h ?? null;
  if (planned !== null && Math.abs(flightH - planned) > PHONE_PLANNED_NOTE_H + 1e-9)
    out.push(`${hoursText(flightH)} flown, ${hoursText(planned)} planned.`);
  return out;
}

export type PhoneFlightState = 'started' | 'landed' | 'finished';

const split = (
  e: Pick<DailyEntry, 'svy_hours' | 'cbx_hours' | 'cal_hours' | 'fry_hours' | 'ext_hours'>,
): number => e.svy_hours + e.cbx_hours + e.cal_hours + e.fry_hours + e.ext_hours;

/** Where a flight entry stands on the phone (§4.3). */
export function phoneFlightState(e: PhoneDailyEntry): PhoneFlightState {
  if (split(e) > 0) return 'finished';
  if (e.flight_time_h != null && e.flight_time_h > 0) return 'landed';
  return 'started';
}

export const PHONE_FLIGHT_STATE_WORDS: Readonly<Record<PhoneFlightState, string>> = {
  started: 'Not landed yet',
  landed: 'Finish on the laptop',
  finished: 'Finished in Teslyn Field',
};

/**
 * Whether the phone may still change a flight (its flight time, and since 2 Nov 2026 every field of "New flight",
 * §4.7): a live flight entry, started on the phone or entered on a laptop, until it is finished in Teslyn Field.
 *
 * "Finished in Teslyn Field" is the hours split: survey, comp box, cals, ferry or external more than 0
 * (`phoneFlightState`). Nothing on `daily_entry` marks an entry complete; the split is the honest signal, because
 * Teslyn Field's Daily entry saves a flight only once `validateDailyEntry` passes, and that needs at least one of
 * those hours. So an entry the laptop has saved is finished, and one only the phone has touched is not.
 */
export function canEditPhoneFlight(e: PhoneDailyEntry): boolean {
  return e.deleted_at === null && e.entry_type === 'flight' && phoneFlightState(e) !== 'finished';
}

/** "Add flight time" is offered on the same flights the phone may change (§4.2). */
export const canAddFlightTime = canEditPhoneFlight;

// ------------------------------------------------------------------------------------- in flight (§4.9, decision 986)

/**
 * The phone's clock as the Flights tab reads it once a minute: its local date (`today` in the app: the crew are where
 * the survey is) and the minutes since that midnight. A flight's take-off time is typed in the same clock ("Now"
 * fills the phone's time), so the two compare directly, with no time zone arithmetic.
 */
export interface PhoneClock {
  readonly date: string;
  readonly minutes: number;
}

/** The clock from a `Date`, in the phone's own zone (local getters only: Hermes has them, and no `Intl` is needed). */
export function phoneClock(now: Date): PhoneClock {
  return { date: localIsoDate(now), minutes: now.getHours() * 60 + now.getMinutes() };
}

/** A take-off (or other) time of day with a day moved on: "01:15", and whether it falls on the next day. */
export interface PhoneLanding {
  /** Whether it is the landing ETA of a flight still up (`eta`) or the landing of one that has landed (`landed`). */
  readonly kind: 'eta' | 'landed';
  /** HH:MM in the clock the take-off was typed in. */
  readonly time: string;
  /** The time falls on the day after the flight's date (a duration that crosses midnight). */
  readonly nextDay: boolean;
}

/** The time of day `hours` after `startMin` minutes past midnight, and how many days on it falls. */
function timeAfter(startMin: number, hours: number): { time: string; days: number } {
  const total = Math.round(startMin + hours * 60);
  const days = Math.floor(total / 1440);
  const m = total - days * 1440;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return { time: `${pad(Math.floor(m / 60))}:${pad(m % 60)}`, days };
}

/**
 * The hours a flight is known to have flown: the flight time typed on the phone, else the laptop's hours split once
 * Teslyn Field has finished it (a laptop flight has no phone flight time), else null (not landed yet).
 */
export function phoneFlownHours(e: PhoneDailyEntry): number | null {
  if (e.flight_time_h != null && e.flight_time_h > 0) return e.flight_time_h;
  if (phoneFlightState(e) === 'finished') return split(e);
  return null;
}

/**
 * The landing of a flight (decision 986): the landing ETA of an unfinished flight, its take-off plus the planned
 * flight time ("Landing ETA 13:45"), or, once it has landed or is finished, the landing it had, its take-off plus
 * the flight time it flew (`phoneFlownHours`; `daily_entry` keeps no landing time, and Teslyn Field's log fill
 * takes the flight time as take-off to landing). A duration past midnight says the next day. Null with no take-off
 * time, or before landing with no planned time: the card shows nothing rather than a placeholder.
 */
export function phoneLanding(e: PhoneDailyEntry): PhoneLanding | null {
  const takeoff = parseTime(e.takeoff_time);
  if (takeoff === null) return null;
  const flown = phoneFlownHours(e);
  if (flown !== null) {
    const t = timeAfter(takeoff, flown);
    return { kind: 'landed', time: t.time, nextDay: t.days >= 1 };
  }
  if (e.planned_flight_h == null || !(e.planned_flight_h > 0)) return null;
  const t = timeAfter(takeoff, e.planned_flight_h);
  return { kind: 'eta', time: t.time, nextDay: t.days >= 1 };
}

/** The landing in words for the card and the details: "Landing ETA 13:45", "Landing ETA 01:15 (next day)", "Landed 17:22". */
export function phoneLandingWords(e: PhoneDailyEntry): string | null {
  const l = phoneLanding(e);
  if (!l) return null;
  return `${l.kind === 'eta' ? 'Landing ETA' : 'Landed'} ${l.time}${l.nextDay ? ' (next day)' : ''}`;
}

/** The minutes from the flight's take-off to the clock (negative before it; null with no take-off time). */
function minutesSinceTakeoff(e: PhoneDailyEntry, clock: PhoneClock): number | null {
  const takeoff = parseTime(e.takeoff_time);
  if (takeoff === null) return null;
  const days = Math.round(
    (isoDateToDate(clock.date).getTime() - isoDateToDate(e.date).getTime()) / 86_400_000,
  );
  return days * 1440 + clock.minutes - takeoff;
}

/**
 * The flight time so far of a flight still up (decision 986): the hours from the entry's take-off time to the
 * phone's clock, as the map's flight line counts them. Null once the flight has landed or is finished (its flight
 * time is shown instead), with no take-off time, before the take-off (a time typed ahead), and more than 12 hours
 * after it (the flight line's own limit, `FLIGHT_STATUS_HOURS`: an entry that old has landed and waits for its
 * flight time). The entry's take-off time is used, never the tracker's: it is the record the crew typed and the one
 * every phone and laptop holds, so the ETA and the time so far agree with the card's take-off.
 */
export function phoneFlightTimeSoFar(e: PhoneDailyEntry, clock: PhoneClock): number | null {
  if (phoneFlightState(e) !== 'started') return null;
  const min = minutesSinceTakeoff(e, clock);
  if (min === null || min < 0 || min > FLIGHT_STATUS_HOURS * 60) return null;
  return min / 60;
}

/** The time so far in the flight line's words, "FT 2.5h" (one decimal, from 0.05 h), or null. */
export function phoneFlightTimeSoFarLabel(e: PhoneDailyEntry, clock: PhoneClock): string | null {
  const h = phoneFlightTimeSoFar(e, clock);
  return h === null ? null : flightTimeLabel(h);
}

/**
 * The same for the "Change flight" form as it is typed (§4.7): the entry with the form's take-off, planned time and
 * flight time laid over it (a take-off that is not a time yet keeps the entry's), so the ETA follows the boxes.
 */
export function phoneFormProgress(
  form: PhoneFlightForm,
  entry: PhoneDailyEntry,
  clock: PhoneClock,
): { readonly landing: string | null; readonly soFar: string | null } {
  const t = typedTime(form.takeoff);
  const e: PhoneDailyEntry = {
    ...entry,
    takeoff_time: t.ok ? t.value : entry.takeoff_time,
    planned_flight_h: typedHours(form.planned),
    flight_time_h: typedHours(form.flight_time ?? ''),
  };
  return { landing: phoneLandingWords(e), soFar: phoneFlightTimeSoFarLabel(e, clock) };
}

// ------------------------------------------------------------------------------------------------ changing (§4.7)

/**
 * A flight entry as the form that changes it shows it (§4.7): the same boxes as "New flight", filled from the entry
 * (the refuel in the aircraft's unit, 0 oil as blank, hours with at most two decimals), and its flight time.
 */
export function phoneFlightFormFromEntry(e: PhoneDailyEntry, a: Aircraft | null): PhoneFlightForm {
  return {
    aircraft_id: e.aircraft_id,
    takeoff: e.takeoff_time ?? '',
    planned: numberText(e.planned_flight_h),
    refuel: phoneRefuelText(e, a),
    fuel_source: e.fuel_source,
    fuel_location: e.fuel_location,
    oil_l: e.oil_l > 0 ? numberText(e.oil_l) : '',
    oil_r: e.oil_r > 0 ? numberText(e.oil_r) : '',
    oil_dipstick_l: numberText(e.oil_dipstick_l),
    oil_dipstick_r: numberText(e.oil_dipstick_r),
    pilot_id: e.pilot_id,
    flight_time: numberText(e.flight_time_h),
    flight_number: e.flt_num !== null ? String(e.flt_num) : '',
  };
}

export interface EditedPhoneFlightInput {
  /** The entry as the phone holds it (the cloud's row with the phone's unsent changes on top). */
  readonly entry: PhoneDailyEntry;
  /** The form as it was opened (`phoneFlightFormFromEntry`). */
  readonly before: PhoneFlightForm;
  /** The form as it is saved; `validatePhoneFlight` accepts it. */
  readonly form: PhoneFlightForm;
  /** The aircraft chosen on the form (null: none, or one no longer in the register). */
  readonly aircraft: Aircraft | null;
}

/**
 * The entry with the form's changes (§4.7): only the boxes the person changed are written, so every other column
 * keeps exactly what it held (a planned 3:20 stays 3.3333 h unless the box is changed), and the outbox sends only
 * those columns against the cloud's version (`buildOp`), as Teslyn Field does. A new aircraft clears what it does
 * not show (`entryForType`: a single keeps no right-engine oil) and reads the refuel in its own fuel unit; a new
 * pilot drops a removed person's kept name and rank (decision 602). Never touches the hours split, the crew beyond
 * the pilot, the locations, the blocks or the comments: those are Teslyn Field's.
 */
export function editedPhoneFlightEntry(input: EditedPhoneFlightInput): PhoneDailyEntry {
  const { entry, before, form, aircraft } = input;
  const next: { -readonly [K in keyof PhoneDailyEntry]: PhoneDailyEntry[K] } = { ...entry };
  const changed = <K extends keyof PhoneFlightForm>(k: K): boolean => (form[k] ?? '') !== (before[k] ?? '');
  const newAircraft = changed('aircraft_id');
  if (newAircraft) next.aircraft_id = form.aircraft_id;
  if (changed('takeoff')) {
    const t = typedTime(form.takeoff);
    if (t.ok) next.takeoff_time = t.value;
  }
  if (changed('planned')) next.planned_flight_h = typedHours(form.planned);
  if (changed('flight_time')) next.flight_time_h = typedHours(form.flight_time ?? '');
  if (changed('refuel') || (newAircraft && form.refuel.trim() !== ''))
    next.fuel_load_l = litresFromFuel(number(form.refuel) ?? 0, aircraft?.fuel_unit ?? 'L');
  if (changed('fuel_source')) next.fuel_source = form.fuel_source;
  if (changed('fuel_location')) next.fuel_location = form.fuel_location.trim();
  if (changed('oil_l')) next.oil_l = number(form.oil_l) ?? 0;
  if (changed('oil_r')) next.oil_r = number(form.oil_r) ?? 0;
  for (const k of ['oil_dipstick_l', 'oil_dipstick_r'] as const) {
    if (!changed(k)) continue;
    const v = dipstickFromText(form[k] ?? '');
    next[k] = v !== null && Number.isFinite(v) ? v : null;
  }
  if (changed('pilot_id')) {
    next.pilot_id = form.pilot_id;
    if ((entry.pilot_name ?? null) !== null) next.pilot_name = null;
    if ((entry.pilot_rank ?? null) !== null) next.pilot_rank = null;
  }
  // R179: the number typed on the phone, or blank again (validatePhoneFlight has refused anything else)
  if (changed('flight_number')) next.flt_num = typedFlightNumber(form.flight_number) ?? null;
  if (!newAircraft) return next;
  const shown = entryForType(next, 'flight', aircraft);
  // a phone flight saved without an aircraft gets the desktop's 1 start once one is chosen (R111), as New flight does
  if (entry.entry_origin === 'mobile' && entry.starts === 0)
    return newFlightEntryDefaults(shown, { aircraft, entries: [], changed: new Set(['fuel_location']) });
  return shown;
}

/**
 * A change made on a form opened on `opened`, laid over the entry as the phone holds it now (`current`): only the
 * columns the change touched. A sync while the form was open (the laptop's take-off, a refreshed row) is kept, so
 * the outbox never sends back a value the phone read before the laptop changed it. `current` null (the entry has
 * gone from the phone meanwhile): the change as it is.
 */
export function rebasePhoneEdit(
  opened: PhoneDailyEntry,
  edited: PhoneDailyEntry,
  current: PhoneDailyEntry | null,
): PhoneDailyEntry {
  if (!current) return edited;
  const same = (a: unknown, b: unknown): boolean =>
    a === b || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));
  const next: Record<string, unknown> = { ...current };
  const was = opened as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(edited)) if (!same(v, was[k])) next[k] = v;
  return next as unknown as PhoneDailyEntry;
}

// ------------------------------------------------------------------------------------------------ details (§4.8)

/** One line of a flight's details: what it is and its value in words ('–' when the entry has none). */
export interface PhoneFlightDetail {
  readonly label: string;
  readonly value: string;
}

export interface PhoneFlightDetailSection {
  readonly title: string;
  readonly rows: readonly PhoneFlightDetail[];
}

const DASH = '–';
const FUEL_SOURCE_WORDS: Readonly<Record<Exclude<FuelSource, null>, string>> = {
  bowser: 'Bowser',
  drum: 'Drum',
  truck: 'Truck',
};
const hoursOr = (h: number | null | undefined): string =>
  h === null || h === undefined ? DASH : hoursText(h);
const litres = (l: number): string => `${numberText(l)} L`;

/**
 * Everything a flight entry holds that the phone has, in words, for the details the phone opens on a tapped flight
 * (§4.8; read only once Teslyn Field has finished it). The phone does not read the project's locations or blocks,
 * so those are counted, not named.
 */
export function phoneFlightDetails(
  e: PhoneDailyEntry,
  ctx: {
    readonly aircraft: readonly Aircraft[];
    readonly people: readonly Pick<Person, 'id' | 'name' | 'active' | 'deleted_at'>[];
    /** The phone's clock, for the flight time so far of a flight still up (§4.9); absent: that row is left out. */
    readonly clock?: PhoneClock | undefined;
  },
): PhoneFlightDetailSection[] {
  const a = ctx.aircraft.find((x) => x.id === e.aircraft_id) ?? null;
  const slot = (id: Uuid | null, kept: string | null | undefined): string =>
    id ? crewPersonName(ctx.people, id) : kept ? removedPersonLabel(kept) : DASH;
  const shape = entryShape('flight', a);
  const twin = shape.oilBoxes === 2;
  const unit = dipstickUnit(a);
  const refuel = phoneRefuelText(e, a);
  const split = [e.svy_hours, e.cbx_hours, e.cal_hours, e.fry_hours, e.ext_hours];
  const total = split.reduce((s, h) => s + h, 0);
  const locations = [e.base_mag_position_id, e.ground_cal_position_id, e.test_line_position_id].filter(
    (x) => x !== null,
  ).length;
  const landing = phoneLanding(e);
  const soFar = ctx.clock ? phoneFlightTimeSoFar(e, ctx.clock) : null;
  return [
    {
      title: 'Flight',
      rows: [
        { label: 'Date', value: `${phoneDateWords(e.date)} (day ${e.jday})` },
        { label: 'Flight number', value: e.flt_num !== null ? String(e.flt_num) : DASH },
        {
          label: 'Aircraft',
          value: a
            ? `${a.registration}${a.type_name ? `, ${a.type_name}` : ''}`
            : e.aircraft_id
              ? 'No longer in the register'
              : DASH,
        },
        { label: 'Take-off', value: e.takeoff_time ?? DASH },
        { label: 'Planned flight time', value: hoursOr(e.planned_flight_h) },
        // the landing ETA while the flight is up, the landing once it is down; nothing without a take-off (§4.9)
        ...(landing
          ? [
              {
                label: landing.kind === 'eta' ? 'Landing ETA' : 'Landed',
                value: `${landing.time}${landing.nextDay ? ' (next day)' : ''}`,
              },
            ]
          : []),
        ...(soFar !== null ? [{ label: 'Flight time so far', value: hoursText(soFar) }] : []),
        { label: 'Flight time (phone)', value: hoursOr(e.flight_time_h) },
        { label: 'State', value: PHONE_FLIGHT_STATE_WORDS[phoneFlightState(e)] },
        { label: 'Started', value: e.entry_origin === 'mobile' ? 'On a phone' : 'In Teslyn Field' },
      ],
    },
    {
      title: 'Crew',
      rows: [
        { label: 'Pilot', value: slot(e.pilot_id, e.pilot_name) },
        { label: 'Pilot 2', value: slot(e.pilot2_id, e.pilot2_name) },
        { label: 'Operator 1', value: slot(e.operator1_id, e.operator1_name) },
        { label: 'Operator 2', value: slot(e.operator2_id, e.operator2_name) },
      ],
    },
    {
      title: 'Hours',
      rows: [
        { label: 'Survey', value: hoursText(e.svy_hours) },
        { label: 'Comp box', value: hoursText(e.cbx_hours) },
        { label: 'Cals', value: hoursText(e.cal_hours) },
        { label: 'Ferry', value: hoursText(e.fry_hours) },
        { label: 'External', value: hoursText(e.ext_hours) },
        { label: 'Total', value: hoursText(total) },
      ],
    },
    {
      title: 'Fuel and oil',
      rows: [
        { label: 'Refuel', value: refuel ? `${refuel} ${phoneFuelUnitLabel(a)}` : DASH },
        { label: 'Refuel source', value: e.fuel_source ? FUEL_SOURCE_WORDS[e.fuel_source] : DASH },
        { label: 'Refuel location', value: e.fuel_location.trim() || DASH },
        { label: 'Fuel used', value: e.fuel_used_l > 0 ? litres(e.fuel_used_l) : DASH },
        {
          label: 'Oil added',
          value: twin ? `Left ${litres(e.oil_l)}, right ${litres(e.oil_r)}` : litres(e.oil_l),
        },
        {
          label: 'Dipstick before oil',
          value: twin
            ? `Left ${dipstickWords(e.oil_dipstick_l, unit)}, right ${dipstickWords(e.oil_dipstick_r, unit)}`
            : dipstickWords(e.oil_dipstick_l, unit),
        },
        { label: 'Engine starts', value: String(e.starts) },
        { label: 'New MR TTIS', value: hoursOr(e.new_mr_ttis_h) },
      ],
    },
    {
      title: 'Comments',
      rows: [
        { label: 'Operational comment', value: e.op_comment.trim() || DASH },
        { label: 'QC comment', value: e.qc_comment.trim() || DASH },
      ],
    },
    {
      title: 'On the laptop',
      rows: [
        { label: 'Blocks touched', value: e.blocks.length > 0 ? String(e.blocks.length) : DASH },
        { label: 'Locations set', value: locations > 0 ? String(locations) : DASH },
        { label: 'Log file', value: e.log_file ?? DASH },
        { label: 'Mobilisation', value: e.mobilization ? 'Yes' : 'No' },
      ],
    },
  ];
}

// ------------------------------------------------------------------------------------------------ ferries (§15.5)

/**
 * The comment that makes a phone flight a ferry: "Ferry", then the route. TJR logged a ferry this way, a flight entry
 * with no flight number, Ferry hours and the route in the comment (decision 235; F26048: "Ferry Griffith to Ceduna"),
 * and Teslyn has no other flight-type field, so a pilot's flight (decision 971) is recorded the same way.
 */
const FERRY_COMMENT = /^Ferry(?:\s|$)/;

/** The longest place name of a ferry's route ("Ceduna", "Forrest / Leonora"). */
export const FERRY_PLACE_MAX = 60;

/** "Ferry", "Ferry Ceduna to Forrest", "Ferry from Ceduna", "Ferry to Forrest". */
export function ferryComment(from: string, to: string): string {
  const a = from.trim().replace(/\s+/g, ' ');
  const b = to.trim().replace(/\s+/g, ' ');
  if (a && b) return `Ferry ${a} to ${b}`;
  if (a) return `Ferry from ${a}`;
  if (b) return `Ferry to ${b}`;
  return 'Ferry';
}

/**
 * The route of a comment `ferryComment` wrote, or null for any other comment (one the operator wrote, say): only a
 * comment that is exactly the route words, each place at most `FERRY_PLACE_MAX` characters.
 */
export function ferryRoute(comment: string): { readonly from: string; readonly to: string } | null {
  const c = comment.trim();
  const route = (from: string, to: string): { from: string; to: string } | null =>
    from.length <= FERRY_PLACE_MAX && to.length <= FERRY_PLACE_MAX && ferryComment(from, to) === c
      ? { from, to }
      : null;
  if (c === 'Ferry') return { from: '', to: '' };
  let m = /^Ferry from (.+)$/.exec(c);
  if (m && !/ to /.test(m[1]!)) return route(m[1]!, '');
  m = /^Ferry to (.+)$/.exec(c);
  if (m) return route('', m[1]!);
  m = /^Ferry (.+?) to (.+)$/.exec(c);
  if (m) return route(m[1]!, m[2]!);
  return null;
}

/**
 * A ferry started on a phone (decision 971): a live flight entry of `entry_origin = 'mobile'` with no flight number,
 * no survey, comp box or cals hours, whose comment starts "Ferry". Numbering it, giving it survey hours or changing
 * the comment in Teslyn Field makes it another flight, as on any entry.
 */
export function isPhoneFerry(
  e: Pick<
    DailyEntry,
    'deleted_at' | 'entry_type' | 'flt_num' | 'op_comment' | 'svy_hours' | 'cbx_hours' | 'cal_hours'
  > &
    Pick<PhoneDailyEntry, 'entry_origin'>,
): boolean {
  return (
    e.deleted_at === null &&
    e.entry_type === 'flight' &&
    e.entry_origin === 'mobile' &&
    e.flt_num === null &&
    e.svy_hours === 0 &&
    e.cbx_hours === 0 &&
    e.cal_hours === 0 &&
    FERRY_COMMENT.test(e.op_comment.trim())
  );
}

/**
 * The Ferry hours Teslyn Field offers for a phone ferry not yet split (its "Put … in Ferry hours" button): the phone's
 * flight time to a tenth; null when it is no phone ferry, has no flight time or already has hours.
 */
export function phoneFerryHours(e: PhoneDailyEntry): number | null {
  if (!isPhoneFerry(e) || split(e) > 0) return null;
  const h = e.flight_time_h ?? null;
  return h !== null && h > 0 ? Math.round(h * 10) / 10 : null;
}

/** What Teslyn Field says about a phone ferry before its hours are in. */
export const PHONE_FERRY_WORDS =
  'It stays a ferry unless you change it: a ferry has no flight number and its flight time goes in Ferry hours. If it was a survey flight, give it its flight number and fill it from its log.';

/**
 * Teslyn Field's note on an entry started on the phone (§4.5), or null for any other entry: what the phone recorded,
 * and whether the desktop's hours split adds up to the phone's flight time (a warning, never a block). A phone ferry
 * not yet split says it is a ferry and how to make it another flight (decision 971).
 */
export function phoneEntryNote(
  e: PhoneDailyEntry,
): { readonly text: string; readonly level: 'info' | 'warn' } | null {
  if (e.entry_origin !== 'mobile') return null;
  const parts: string[] = [];
  if (e.planned_flight_h != null) parts.push(`planned ${hoursText(e.planned_flight_h)}`);
  if (e.flight_time_h != null) parts.push(`flight time ${hoursText(e.flight_time_h)}`);
  const total = split(e);
  // decision 971: a pilot's flight is a ferry until the operator says otherwise
  if (isPhoneFerry(e) && total <= 0)
    return {
      text: `Started on a phone as a ferry flight${parts.length ? `: ${parts.join(', ')}` : ''}. ${PHONE_FERRY_WORDS}`,
      level: 'info',
    };
  const head = `Started on a phone${parts.length ? `: ${parts.join(', ')}` : ''}.`;
  if (e.flight_time_h == null)
    return { text: `${head} The flight time is added on the phone after landing.`, level: 'info' };
  if (total <= 0)
    return {
      text: `${head} Split the flight time into Survey, Comp box, Cals, Ferry and External.`,
      level: 'info',
    };
  if (Math.abs(total - e.flight_time_h) > PHONE_HOURS_TOLERANCE_H + 1e-9)
    return {
      text: `${head} The hours add up to ${hoursText(total)}; the phone says ${hoursText(e.flight_time_h)} flown.`,
      level: 'warn',
    };
  return { text: `${head} The hours add up to the phone's ${hoursText(e.flight_time_h)}.`, level: 'info' };
}

/** Today's flight entries of the project on the phone, latest take-off first; deleted and other days left out. */
export function phoneFlightsOfDay<E extends PhoneDailyEntry>(entries: readonly E[], date: string): E[] {
  return phoneFlightList(entries.filter((e) => e.date === date));
}

/**
 * The order of the phone's Flights tab (§4.6): newest first by date, then take-off time, then flight number (a
 * flight with no take-off time or number comes after those of its day that have one); then the id, so the order
 * is the same on every phone.
 */
export function comparePhoneFlights(
  a: Pick<PhoneDailyEntry, 'id' | 'date' | 'takeoff_time' | 'flt_num'>,
  b: Pick<PhoneDailyEntry, 'id' | 'date' | 'takeoff_time' | 'flt_num'>,
): number {
  if (a.date !== b.date) return a.date < b.date ? 1 : -1;
  const t = (parseTime(b.takeoff_time) ?? -1) - (parseTime(a.takeoff_time) ?? -1);
  if (t !== 0) return t;
  const n = (b.flt_num ?? -1) - (a.flt_num ?? -1);
  if (n !== 0) return n;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Every flight entry of the project the phone holds (synced, or made on the phone), newest first
 * (`comparePhoneFlights`); deleted entries and the other entry types (standby, weather and so on) left out.
 */
export function phoneFlightList<E extends PhoneDailyEntry>(entries: readonly E[]): E[] {
  return entries.filter((e) => e.deleted_at === null && e.entry_type === 'flight').sort(comparePhoneFlights);
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/** A `YYYY-MM-DD` date in words for the phone's list: "Sat 17 Oct 2026" (no locale: the same on every phone). */
export function phoneDateWords(date: string): string {
  const d = isoDateToDate(date);
  if (Number.isNaN(d.getTime())) return date;
  return `${DAY_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** The heading of a day in the phone's list: "Today, Sat 17 Oct 2026", "Yesterday, Fri 16 Oct 2026", else the date. */
export function phoneDayLabel(date: string, today: string): string {
  const words = phoneDateWords(date);
  if (date === today) return `Today, ${words}`;
  if (date === addIsoDays(today, -1)) return `Yesterday, ${words}`;
  return words;
}

export interface PhoneFlightDay<E extends PhoneDailyEntry = PhoneDailyEntry> {
  /** `YYYY-MM-DD`. */
  readonly date: string;
  /** `phoneDayLabel`. */
  readonly label: string;
  /** The day's flights, latest take-off first. */
  readonly flights: readonly E[];
}

/** The phone's flight list by day, newest day first; a day with no flight entry is not listed. */
export function phoneFlightDays<E extends PhoneDailyEntry>(
  entries: readonly E[],
  today: string,
): PhoneFlightDay<E>[] {
  const days: PhoneFlightDay<E>[] = [];
  let current: { date: string; label: string; flights: E[] } | null = null;
  for (const e of phoneFlightList(entries)) {
    if (!current || current.date !== e.date) {
      current = { date: e.date, label: phoneDayLabel(e.date, today), flights: [] };
      days.push(current);
    }
    current.flights.push(e);
  }
  return days;
}

/** "1 flight" or "14 flights". */
export function phoneFlightCount(n: number): string {
  return `${n} flight${n === 1 ? '' : 's'}`;
}

// ------------------------------------------------------------------------------------------------ push (§6.1)

export type MobilePlatform = 'android' | 'ios';

/**
 * Whether a native push token looks right before it is stored: an FCM registration token (Android: letters, digits,
 * `-`, `_` and `:`, 100 to 4096 characters) or an APNs device token (iPhone: 64 to 200 hex digits).
 */
export function mobilePushTokenValid(platform: MobilePlatform, token: string): boolean {
  if (platform === 'ios') return /^[0-9a-fA-F]{64,200}$/.test(token);
  return /^[A-Za-z0-9_:-]{100,4096}$/.test(token);
}

/**
 * Why "Turn on alerts" did not finish on this phone (§6.1): the person refused the phone's permission; this build
 * carries no push set-up (an Android build made without the Firebase file, `google-services.json`); the phone gave
 * no push address for another reason; the address does not look right; Teslyn Cloud did not register the phone.
 */
export type PushSetupFailure =
  'notifications_off' | 'push_not_in_build' | 'no_push_address' | 'bad_push_address' | 'not_registered';

/**
 * Whether the phone's push address error is the one a build without Firebase gives (Expo: "Unable to get Firebase
 * Messaging instance. Did you configure `googleServicesFile` ... Default FirebaseApp is not initialized"), so the
 * Alerts tab says push is not in the build rather than repeating it.
 */
export function pushErrorMeansNoFirebase(detail: string): boolean {
  return /firebase|googleServicesFile|google-services/i.test(detail);
}

/** Who is told what is missing when push is not in the build: tenant admins set Teslyn up (ADR-016 §1). */
export const PUSH_SETUP_ROLES: readonly MobileStaffRole[] = ['tenant_admin'];

export function canSetUpPush(roles: readonly string[]): boolean {
  return roles.some((r) => (PUSH_SETUP_ROLES as readonly string[]).includes(r));
}

/** What the Alerts tab says after "Turn on alerts" did not finish. */
export interface PushSetupWords {
  /** One plain sentence or two for everyone. Never the phone's or the server's own error text. */
  readonly message: string;
  /** A second line for the people who set things up (`canSetUpPush`), naming what is missing; null otherwise. */
  readonly setup: string | null;
}

const ALERTS_STILL_HERE = 'Alerts still show here when you open the app.';

const PUSH_FAILURE_WORDS: Readonly<Record<PushSetupFailure, string>> = {
  notifications_off:
    'Notifications are off for Teslyn on this phone. Turn them on in the phone’s settings, then try again.',
  push_not_in_build: `Push alerts are not switched on in this Teslyn Mobile build yet. ${ALERTS_STILL_HERE}`,
  no_push_address: `This phone could not get a push address. ${ALERTS_STILL_HERE} Try again later.`,
  bad_push_address: `The phone gave a push address Teslyn does not recognise. ${ALERTS_STILL_HERE}`,
  not_registered: `Teslyn Cloud did not register this phone for push alerts. Check the signal and try again. ${ALERTS_STILL_HERE}`,
};

const PUSH_SETUP_MISSING: Readonly<Partial<Record<PushSetupFailure, string>>> = {
  push_not_in_build:
    'Firebase is not set up for Teslyn Mobile yet; the set-up guide’s Firebase section (Android push) covers it.',
};

/**
 * The words the Alerts tab shows for a failure: plain sentences for everyone and, for the people who set things up,
 * a second line naming what is missing. Technical detail (the phone's or the server's error text) is for the device
 * log only, never the screen.
 */
export function pushSetupWords(failure: PushSetupFailure, roles: readonly string[]): PushSetupWords {
  return {
    message: PUSH_FAILURE_WORDS[failure],
    setup: canSetUpPush(roles) ? (PUSH_SETUP_MISSING[failure] ?? null) : null,
  };
}

/**
 * The phone's name in the device list before 4 Nov 2026: "Pixel 8 (Android 15)", at most 80 characters. On a Samsung
 * the "OS name" expo-device gives is Android's `Build.VERSION.BASE_OS`, the whole build fingerprint
 * ("samsung/pa3qxxx/pa3q:17/CP2A.260605.016/…"), so MB's phone was listed as "SM-S938B (samsung/pa3qxxx/…".
 * Kept for the seat check-in's label; the device list uses `mobileDeviceName`.
 */
export function mobileDeviceLabel(model: string | null, os: string | null, version: string | null): string {
  const name = (model ?? '').trim() || 'Phone';
  const sys = [os, version].filter((s) => s && s.trim()).join(' ');
  return (sys ? `${name} (${sys})` : name).slice(0, 80);
}

/** The longest automatic label of a phone (`mobile_device.label`). */
export const MOBILE_DEVICE_LABEL_MAX = 80;

/**
 * The phone's friendly name in the device list (MB, 4 Nov 2026): the maker and the model, "Samsung SM-S938B",
 * "Google Pixel 8", "Apple iPhone 15". The maker is capitalised as a word ("samsung" is what Android reports); a maker
 * the model already starts with is not repeated ("Apple" + "Apple Watch"). No OS, no version, no fingerprint.
 */
export function mobileDeviceName(
  manufacturer: string | null | undefined,
  model: string | null | undefined,
): string {
  const maker = (manufacturer ?? '').trim().replace(/\s+/g, ' ');
  const name = (model ?? '').trim().replace(/\s+/g, ' ');
  const makerWord = maker ? maker[0]!.toUpperCase() + maker.slice(1) : '';
  if (!name) return (makerWord ? `${makerWord} phone` : 'Phone').slice(0, MOBILE_DEVICE_LABEL_MAX);
  if (!makerWord || name.toLowerCase().startsWith(maker.toLowerCase()))
    return name.slice(0, MOBILE_DEVICE_LABEL_MAX);
  return `${makerWord} ${name}`.slice(0, MOBILE_DEVICE_LABEL_MAX);
}

/** The longest hardware key of a phone (`mobile_device.hardware`). */
export const MOBILE_HARDWARE_MAX = 120;

/**
 * What tells one physical phone from another under one sign-in (`mobile_device.hardware`): the platform, the maker,
 * the model and the first 16 hex characters of the SHA-256 of the install id (Android's app-scoped id, the iPhone's
 * vendor id), so two phones of the same model under one sign-in stay apart, and a phone that lost its stored id
 * (reinstalled, data cleared) is still known as the same one: `mobile_device_register` retires the person's older
 * rows of the same app and hardware. Lower case, no spaces. Without an install id the key names the model only.
 */
export function mobileHardwareKey(
  platform: MobilePlatform,
  manufacturer: string | null | undefined,
  model: string | null | undefined,
  installHashHex: string | null | undefined,
): string {
  const part = (s: string | null | undefined): string =>
    (s ?? '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9.+-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'unknown';
  const hash = (installHashHex ?? '').trim().toLowerCase();
  const tail = /^[0-9a-f]{16,}$/.test(hash) ? `/${hash.slice(0, 16)}` : '';
  return `${platform}/${part(manufacturer)}/${part(model)}${tail}`.slice(0, MOBILE_HARDWARE_MAX);
}

/**
 * The stable id of this install, as a UUID, from the SHA-256 (hex) of `teslyn-mobile:<app id>:<install id>`: the
 * same phone, app and install id give the same device id after an update, a reinstall or cleared data, so
 * `mobile_device` keeps one row (MB's phone had four, 4 Nov 2026: a random id made afresh at each reinstall). Beta
 * and stable are two apps, so each has its own id on one phone. null when the hash is not one (no install id: the
 * app keeps a random id, as before).
 */
export function mobileStableDeviceId(sha256Hex: string | null | undefined): string | null {
  const h = (sha256Hex ?? '').trim().toLowerCase();
  if (!/^[0-9a-f]{64}$/.test(h)) return null;
  // the shape of a version 4 uuid, so it passes every uuid check the server and the sync ops make
  const variant = (8 + (parseInt(h[16]!, 16) & 3)).toString(16);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-4${h.slice(13, 16)}-${variant}${h.slice(17, 20)}-${h.slice(20, 32)}`;
}

/** The name of the app a `mobile_device` row came from: "Teslyn Mobile (beta)" or "Teslyn Mobile" (unknown too). */
export function mobileAppNameOf(appId: string | null | undefined): string {
  return appId === MOBILE_APP_IDS.beta ? mobileAppName('beta') : mobileAppName('stable');
}

// ------------------------------------------------------------------------------------------------ push on this phone (§6.1)

/** What the phone remembers of the person's choice: alerts turned on here, turned off here, or never asked. */
export type PhonePushChoice = 'on' | 'off' | null;

export interface PhonePushFacts {
  /** The person's remembered choice on this phone. */
  readonly choice: PhonePushChoice;
  /** Whether Android or iOS lets Teslyn show notifications. */
  readonly allowed: boolean;
  /** Whether this build carries push at all (an Android build made with the Firebase file). */
  readonly inBuild: boolean;
}

export type PhonePushState = 'on' | 'off' | 'blocked' | 'not_registered' | 'not_in_build';

export interface PhonePushStatus {
  readonly state: PhonePushState;
  /** "Alerts on this phone: On" and the rest of the line. */
  readonly line: string;
  /** A sentence under the line. */
  readonly note: string;
  /** The switch offered: turn alerts on, turn them off, or none (a build without push). */
  readonly action: 'turn_on' | 'turn_off' | null;
  /** Offer the button to the phone's notification settings (the phone blocks Teslyn's notices). */
  readonly settings: boolean;
}

/**
 * What the Alerts tab says about push on this phone, always (MB, 4 Nov 2026: "when I go to Alerts in the mobile app
 * there is no option for that"), and which switch it offers. The person's own "off" wins over everything: the app
 * never turns alerts back on by itself after it (`registerPhone` keeps the choice).
 */
export function phonePushStatus(f: PhonePushFacts): PhonePushStatus {
  if (f.choice === 'off')
    return {
      state: 'off',
      line: 'Alerts on this phone: Off',
      note: `You turned alerts off on this phone. ${ALERTS_STILL_HERE}`,
      action: 'turn_on',
      settings: false,
    };
  if (!f.inBuild)
    return {
      state: 'not_in_build',
      line: 'Alerts on this phone: Off',
      note: PUSH_FAILURE_WORDS.push_not_in_build,
      action: null,
      settings: false,
    };
  if (!f.allowed)
    return {
      state: 'blocked',
      line: 'Alerts on this phone: Off, notifications are blocked in the phone’s settings',
      note: 'Allow notifications for Teslyn Mobile in the phone’s settings, then turn alerts on here.',
      action: 'turn_on',
      settings: true,
    };
  if (f.choice === 'on')
    return {
      state: 'on',
      line: 'Alerts on this phone: On',
      note: 'This phone receives the alerts you chose on Teslyn Cloud’s Alerts page.',
      action: 'turn_off',
      settings: false,
    };
  return {
    state: 'not_registered',
    line: 'Alerts on this phone: Off, not registered yet',
    note: 'Turn alerts on and this phone receives the alerts you chose on Teslyn Cloud’s Alerts page.',
    action: 'turn_on',
    settings: false,
  };
}

// ------------------------------------------------------------------------------------------------ chart (§5.2)

/** The most points a station chart draws on a phone. */
export const MOBILE_CHART_POINTS = 600;

/**
 * Readings thinned to at most `max` points for a phone chart, keeping each bucket's lowest and highest reading so a
 * spike is never thinned away; bad readings are kept as gaps (null).
 */
export function thinReadings(
  rows: readonly { readonly ms: number; readonly nt: number | null }[],
  max: number = MOBILE_CHART_POINTS,
): { ms: number; nt: number | null }[] {
  const sorted = rows.slice().sort((a, b) => a.ms - b.ms);
  if (sorted.length <= max) return sorted.map((r) => ({ ms: r.ms, nt: r.nt }));
  const buckets = Math.max(1, Math.floor(max / 2));
  const size = sorted.length / buckets;
  const out: { ms: number; nt: number | null }[] = [];
  for (let b = 0; b < buckets; b++) {
    const part = sorted.slice(Math.floor(b * size), Math.floor((b + 1) * size));
    if (part.length === 0) continue;
    const good = part.filter((r) => r.nt !== null && Number.isFinite(r.nt));
    if (good.length === 0) {
      out.push({ ms: part[0]!.ms, nt: null });
      continue;
    }
    let lo = good[0]!;
    let hi = good[0]!;
    for (const r of good) {
      if ((r.nt as number) < (lo.nt as number)) lo = r;
      if ((r.nt as number) > (hi.nt as number)) hi = r;
    }
    const pair = lo.ms <= hi.ms ? [lo, hi] : [hi, lo];
    for (const r of lo === hi ? [lo] : pair) out.push({ ms: r.ms, nt: r.nt });
    if (good.length < part.length) out.push({ ms: part[part.length - 1]!.ms, nt: null });
  }
  return out;
}

/**
 * Buckets a station chart asks the database for (`station_readings_chart`, 20261030153000): the window is split in
 * this many equal parts and each answers with its lowest and highest good reading, so the phone makes one request
 * for a chart however many readings the station logged (a 1 s unit logs 86,400 a day), and the thinned chart is
 * the same shape as `thinReadings` gives (two points a bucket, at most `MOBILE_CHART_POINTS`).
 */
export const MOBILE_CHART_BUCKETS = MOBILE_CHART_POINTS / 2;

/** One bucket of `station_readings_chart`: the lowest and highest good reading, and how many were bad. */
export interface ReadingBucket {
  /** The bucket's number from the window's start (0 first); a missing number is a gap with no readings. */
  readonly b: number;
  readonly lo_ms: number | null;
  readonly lo_nt: number | null;
  readonly hi_ms: number | null;
  readonly hi_nt: number | null;
  /** The newest reading's time in the bucket (where a gap after bad readings is drawn). */
  readonly last_ms: number;
  readonly bad: number;
  readonly n: number;
}

export interface ChartBuckets {
  /** Readings in the window, good and bad. */
  readonly total: number;
  readonly buckets: readonly ReadingBucket[];
}

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** The database's answer as buckets, tolerating anything missing or malformed (an empty chart, never a throw). */
export function readChartBuckets(raw: unknown): ChartBuckets {
  const a = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const buckets: ReadingBucket[] = [];
  for (const x of Array.isArray(a.buckets) ? a.buckets : []) {
    if (!x || typeof x !== 'object') continue;
    const o = x as Record<string, unknown>;
    const b = finite(o.b);
    const last = finite(o.last_ms);
    if (b === null || last === null) continue;
    buckets.push({
      b,
      lo_ms: finite(o.lo_ms),
      lo_nt: finite(o.lo_nt),
      hi_ms: finite(o.hi_ms),
      hi_nt: finite(o.hi_nt),
      last_ms: last,
      bad: finite(o.bad) ?? 0,
      n: finite(o.n) ?? 0,
    });
  }
  buckets.sort((x, y) => x.b - y.b);
  return { total: finite(a.total) ?? buckets.reduce((s, x) => s + x.n, 0), buckets };
}

/**
 * The chart's points from the buckets, the same shape `thinReadings` gives: each bucket's lowest and highest in time
 * order; a gap (null) where a bucket holds bad readings, where it holds no good reading, and where buckets are
 * missing between two that have readings (the station was silent), so the line never bridges a silence.
 */
export function chartPointsFromBuckets(
  buckets: readonly ReadingBucket[],
): { ms: number; nt: number | null }[] {
  const out: { ms: number; nt: number | null }[] = [];
  let prev: ReadingBucket | null = null;
  for (const k of buckets) {
    if (prev && k.b > prev.b + 1 && out.length > 0 && out[out.length - 1]!.nt !== null)
      out.push({ ms: prev.last_ms, nt: null });
    const hasGood = k.lo_ms !== null && k.lo_nt !== null && k.hi_ms !== null && k.hi_nt !== null;
    if (!hasGood) {
      out.push({ ms: k.last_ms, nt: null });
    } else {
      const lo = { ms: k.lo_ms!, nt: k.lo_nt! };
      const hi = { ms: k.hi_ms!, nt: k.hi_nt! };
      if (lo.ms === hi.ms) out.push(lo);
      else out.push(...(lo.ms < hi.ms ? [lo, hi] : [hi, lo]));
      if (k.bad > 0) out.push({ ms: k.last_ms, nt: null });
    }
    prev = k;
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ map trail (§5.1)

/** The most points of one aircraft's trail a phone's web map draws. */
export const MOBILE_TRAIL_POINTS = 1500;

/**
 * An aircraft's trail for the phone's map: the points 20 m or 10 s apart (`thinTrail`, as the laptop's map keeps a
 * trail), then at most `max` of them spread evenly, always keeping the first and the newest. A 12-hour trail of
 * 1 s fixes (43,200 points) is drawn from 1,500, which a phone pans and zooms without a stutter; the trail's shape
 * is kept, only the dense middle thins.
 */
export function phoneTrail(
  points: readonly { readonly lat: number; readonly lon: number; readonly timeMs: number }[],
  max: number = MOBILE_TRAIL_POINTS,
): { readonly lat: number; readonly lon: number; readonly timeMs: number }[] {
  const thinned = thinTrail(
    points.map((p) => ({
      lat: p.lat,
      lon: p.lon,
      timeMs: p.timeMs,
      altFt: null,
      speedKt: null,
      headingDeg: null,
    })),
  );
  if (thinned.length <= max || max < 2)
    return thinned.map((p) => ({ lat: p.lat, lon: p.lon, timeMs: p.timeMs }));
  const out: { lat: number; lon: number; timeMs: number }[] = [];
  const step = (thinned.length - 1) / (max - 1);
  for (let i = 0; i < max; i++) {
    const p = thinned[Math.round(i * step)]!;
    out.push({ lat: p.lat, lon: p.lon, timeMs: p.timeMs });
  }
  return out;
}

// ------------------------------------------------------------------------------------------------ mobile seat (§3.1)

/** The phone's seat, from `licence-checkin` with `device_kind: 'mobile'` (decisions 289, 290; licences-page.md §5). */
export type MobileSeat =
  | { readonly state: 'not_checked'; readonly words: string }
  | { readonly state: 'active'; readonly words: string }
  | { readonly state: 'refused'; readonly words: string };

/** The seat check is off in this environment (decision 866): nothing is asked or refused. */
export const MOBILE_SEAT_OFF: MobileSeat = {
  state: 'not_checked',
  words: 'The seat check is off. Each staff licence covers two phones.',
};

/** A pilot-only sign-in's phone (decision 918): no seat, two phones. */
export const PILOT_PHONE_WORDS = 'Pilot sign-ins take no licence seat. Each pilot may use two phones.';

/**
 * What a `licence-checkin` answer means for this phone, in words; null (no answer, a proxy page, no signal) is never
 * a refusal, as on the laptop.
 */
export function mobileSeatFromAnswer(
  answer:
    | { readonly status: 'active' }
    | { readonly status: 'no_seat'; readonly seats: number; readonly in_use: number }
    | { readonly status: 'device_limit'; readonly limit: number }
    | { readonly status: string; readonly pilot?: boolean }
    | null,
): MobileSeat {
  if (!answer)
    return { state: 'not_checked', words: 'The seat could not be checked. The app keeps working.' };
  switch (answer.status) {
    case 'active':
      return 'pilot' in answer && answer.pilot === true
        ? { state: 'active', words: `This phone is on your pilot sign-in. ${PILOT_PHONE_WORDS}` }
        : { state: 'active', words: 'This phone is on your seat (each seat covers two phones).' };
    case 'no_seat': {
      const a = answer as { seats: number; in_use: number };
      return {
        state: 'refused',
        words: `Every seat of your organisation’s licence is in use (${a.in_use} of ${a.seats}). Ask your tenant admin to free one on the Licences page.`,
      };
    }
    case 'device_limit': {
      const a = answer as { limit: number; pilot?: boolean };
      return {
        state: 'refused',
        words: `Your ${a.pilot === true ? 'pilot sign-in' : 'seat'} already has ${a.limit} ${a.limit === 1 ? 'phone' : 'phones'}. Release one on Teslyn Cloud’s Licences page (your tenant admin can), then open the app again.`,
      };
    }
    case 'released':
    case 'revoked':
      return {
        state: 'refused',
        words:
          'This phone was released from your seat. Open the app again to take it back, or ask your tenant admin.',
      };
    case 'not_member':
    case 'removed':
      return { state: 'refused', words: 'You are no longer staff of this organisation in Teslyn.' };
    default:
      return {
        state: 'refused',
        words:
          'Your organisation has no Teslyn licence at the moment. Ask your tenant admin to look at the Licences page.',
      };
  }
}
